/**
 * todo
 * 4.测距、面积相关的参考https://www.jianshu.com/p/00f39ea223cf
 */
import * as THREE from 'three'
import { Camera } from './Camera'
import { Listener } from './Listener'
// import { EventEmitter } from 'events/events'

class MPoint {
  /**
   * 一个点构造方法
   * @param {number} x x坐标
   * @param {number} y y坐标
   */
  constructor (x, y) {
    /**
     * @type {number} 二维x轴
     */
    this.x = x
    /**
     * @type {number} 二维y轴
     */
    this.y = y
  }
}

class Area {
  /**
   * 一片区域构造函数
   * @param {Element} canvasDOM 测量canvas的DOM元素
   * @param {Camera} cam 相机实例
   */
  constructor (canvasDOM, cam) {
    /**
     * @type {Element} canvasDOM元素
     */
    this.canvasDOM = canvasDOM
    /**
     * @type {CanvasRenderingContext2D} canvas 2D context
     */
    this.ctx = canvasDOM.getContext('2d')
    /**
     * @type {MPoint[]} 2d canvas的点集合，应该由3D点计算得到
     */
    this.pointSet = []
    /**
     * @type {THREE.Vector3} 保存的3D物理坐标点
     */
    this.physicalPointSet = []
    /**
     * @type {boolean} 是否已经画完
     */
    this.done = false
    /**
     * @type {number} 当前绘制多边形的面积
     */
    this.square = 0
    /**
     * @type {number} 单位，0代表单位为m，1代表单位为英尺ft，默认为0
     */
    this.unit = 0
    /**
     * @type {Camera} 相机封装
     */
    this.cam = cam
  }

  /**
   * 在面积实例中添加一个点
   * @param {number} x x坐标
   * @param {number} y y坐标
   */
  addMPoint (x, y) {
    const physicalPoint = this.getPhysicalPoint(x, y) // 获取物理坐标点
    this.physicalPointSet.push(physicalPoint) // 物理坐标点+1
    const canvasPoint = this.getCanvasPoint(physicalPoint) // 转换回canvas二维点
    this.pointSet.push(new MPoint(canvasPoint.x, canvasPoint.y)) // 二维点+1
  }

  /**
   * 弹出一个点
   */
  popMPoint () {
    this.physicalPointSet.pop()
    this.pointSet.pop()
  }

  /**
   * 相机变换后更新面积的二维点位置
   */
  updatePointSet () {
    for (let i = 0; i < this.physicalPointSet.length; ++i) {
      const canvasPoint = this.getCanvasPoint(this.physicalPointSet[i])
      this.pointSet[i].x = canvasPoint.x
      this.pointSet[i].y = canvasPoint.y
    }
  }

  /**
   * 鼠标输入二维点xy，得到对应物理坐标Vector3
   * @param {number} x 二维点x坐标
   * @param {number} y 二维点y坐标
   * @returns {THREE.Vector3} 返回三维点坐标
   */
  getPhysicalPoint (x, y) {
    const cam = this.cam
    const vec = new THREE.Vector3()
    vec.set(
      (x / this.canvasDOM.width) * 2 - 1,
      -(y / this.canvasDOM.height) * 2 + 1,
      0)
    vec.unproject(cam.camera)
    return vec
  }

  /**
   * 输入物理Vector3，得到2D坐标
   * @param {THREE.Vector3} pos 三维坐标
   * @returns {{x:number,y:number}} 返回二维坐标对象
   */
  getCanvasPoint (pos) {
    const cam = this.cam
    const ppp = pos.clone()

    const standardVector = ppp.project(cam.camera)
    const x = (standardVector.x + 1) * this.canvasDOM.width / 2
    const y = (-standardVector.y + 1) * this.canvasDOM.height / 2

    return { x: x, y: y }
  }

  /**
   * 计算三维点间的距离
   * @param {THREE.Vector3} a 点A
   * @param {THREE.Vector3} b 点B
   * @returns {Number} 返回距离值
   */
  getDistance (a, b) {
    return a.distanceTo(b) / 1000
  }

  /**
   * 获取当前区域面积
   * @returns {number} 返回面积值
   */
  getSquare () {
    let tempSum = 0
    const points = this.physicalPointSet

    const length = points.length
    if (length < 3) return
    for (let i = 0; i < length - 1; ++i) {
      tempSum += (points[i].x * points[i + 1].z - points[i + 1].x * points[i].z)
    }

    return (Math.abs(tempSum + (points[length - 1].x * points[0].z) - (points[0].x * points[length - 1].z))) / 2000000
  }

  /**
   * 绘制此区域
   */
  drawArea () {
    this.drawPoints()
    this.drawLines()
    this.drawText()
  }

  /**
   * 绘制点
   */
  drawPoints () {
    // 画点
    for (let i = 0; i < this.pointSet.length; ++i) {
      this.ctx.save()
      this.ctx.beginPath()
      this.ctx.arc(this.pointSet[i].x, this.pointSet[i].y, 6, 0, 2 * Math.PI)
      this.ctx.fillStyle = 'rgb(67,205,218)'
      this.ctx.fill()
      this.ctx.restore()
    }
  }

  /**
   * 绘制线
   */
  drawLines () {
    // 静态线
    if (this.pointSet.length < 2) return
    for (let i = 0; i < this.pointSet.length; ++i) {
      this.ctx.save()
      this.ctx.beginPath()
      this.ctx.moveTo(this.pointSet[i].x, this.pointSet[i].y)
      this.ctx.lineTo(this.pointSet[(i + 1) % this.pointSet.length].x, this.pointSet[(i + 1) % this.pointSet.length].y)
      this.ctx.strokeStyle = 'rgb(67,205,218)'
      this.ctx.lineWidth = 1
      this.ctx.closePath()
      this.ctx.stroke()
      this.ctx.restore()
    }
  }

  /**
   * 绘制文字
   */
  drawText () {
    // 长度
    if (this.pointSet.length <= 1) return
    for (let i = 0; i < this.pointSet.length; ++i) {
      const len = this.getDistance(this.physicalPointSet[i], this.physicalPointSet[(i + 1) % this.pointSet.length])
      const lengthText = this.setUnit(len, true)
      const x = (this.pointSet[i].x + this.pointSet[(i + 1) % this.pointSet.length].x) / 2
      const y = (this.pointSet[i].y + this.pointSet[(i + 1) % this.pointSet.length].y) / 2
      this.drawCanvasTextStyle(lengthText, x, y, 'white')
    }

    // 面积
    if (this.pointSet.length <= 2) return
    // 位置
    let tempx = 0
    let tempy = 0
    for (let i = 0; i < this.pointSet.length; ++i) {
      tempx += this.pointSet[i].x
      tempy += this.pointSet[i].y
    }
    const ax = tempx / this.pointSet.length
    const ay = tempy / this.pointSet.length
    // 数值
    if (!this.done) this.square = this.getSquare()
    const squareText = this.setUnit(this.square, false)
    // 样式
    this.drawCanvasTextStyle(squareText, ax, ay, 'rgb(180,220,180)')
  }

  /**
   * 设置单位
   * @param {number} number 数值
   * @param {boolean} isLength 是否为长度
   */
  setUnit (number, isLength) {
    switch (this.unit) {
      case 0:
        if (isLength) return number.toFixed(2) + 'm'
        else return number.toFixed(1) + 'm²'
      case 1:
        if (isLength) return (number * 3.2808).toFixed(2) + 'ft'
        else return (number * 10.76).toFixed(1) + 'ft²'
    }
  }

  /**
   * 绘制canvas文字
   * @param {string} Text 文字内容
   * @param {number} x x坐标
   * @param {number} y y坐标
   * @param {string} fillColor 填充颜色
   */
  drawCanvasTextStyle (Text, x, y, fillColor) {
    this.ctx.save()
    this.ctx.lineWidth = 2.5
    this.ctx.font = 'bold 12px sans-serif'
    this.ctx.strokeStyle = 'black'
    this.ctx.textBaseLine = 'middle'
    this.ctx.textAlign = 'center'
    this.ctx.strokeText(Text, x, y)
    this.ctx.font = 'bold 12px sans-serif'
    this.ctx.fillStyle = fillColor
    this.ctx.textBaseLine = 'middle'
    this.ctx.textAlign = 'center'
    this.ctx.fillText(Text, x, y)
    this.ctx.restore()
  }
}

export class Measure {
  /**
   * 初始化测量模块
   * @param {{platform:String,browser:String}} ua 运行平台信息
   * @param {Camera} camera 相机封装，用于计算真实位置
   */
  constructor (ua, camera) {
    /**
     * @type {Element} canvasDOM元素
     */
    this.canvasDOM = null
    /**
     * @type {CanvasRenderingContext2D} canvas 2D context
     */
    this.ctx = null
    /**
     * @type {boolean} 是否处于测量模式，update里是否更新
     */
    this.measureMode = false
    /**
     * @type {boolean} 后台编辑是否开启测量模式
     */
    this.showMeasure = true
    /**
     * @type {{platform:String,browser:String}} 运行平台信息
     */
    this.ua = ua
    /**
     * @type {Listener} 出现在绘制中的选项
     */
    this.startPaintListener = new Listener()
    /**
     * @type {Listener} 出现在绘制中的选项
     */
    this.endPaintListener = new Listener()
    /**
     * @type {boolean} 是否在canvas内，update要求为true
     */
    this.inCanvas = true
    /**
     * @type {number} PC端光标屏幕位置 X轴
     */
    this.cursorX = -65536
    /**
     * @type {number} PC端光标屏幕位置 Y轴
     */
    this.cursorY = -65536
    /**
     * @type {boolean} 是否需要重新绘制帧，true则update内重绘
     */
    this.paint = false
    /**
     * @type {Area[]} 保存的已经画好的区域，不再更新
     */
    this.AreaSet = []
    /**
     * @type {Area} 当前正在画的区域
     */
    this.currArea = null
    /**
     * @type {{hotpoint:Symbol,free:Symbol,None:Symbol}} 状态枚举
     */
    this.enumState = Object.freeze({
      painting: Symbol('painting'),
      free: Symbol('free')
    })
    /**
     * @type {symbol} 光标状态
     */
    this.state = this.enumState.free

    /**
     * @type {Camera} 相机实例
     */
    this.camera = camera

    /**
     * @type {function} 触屏事件
     */
    this.bindTouch = this.onTouchStart.bind(this)
    /**
     * @type {function} 拖拽事件
     */
    this.bindDrag = this.onDrag.bind(this)
    /**
     * @type {function} 触屏抬起事件
     */
    this.bindTouchEnd = this.onTouchEnd.bind(this)

    /**
     * @type {function} 鼠标移动事件
     */
    this.bindMouseMove = this.onMouseMove.bind(this)
    /**
     * @type {function} 鼠标按下事件
     */
    this.bindMouseDown = this.onMouseDown.bind(this)
    /**
     * @type {function} 鼠标弹起事件
     */
    this.bindMouseUp = this.onMouseUp.bind(this)
  }

  /**
   * 初始化方法
   */
  init () {
    // if (!this.showMeasure) return

    this.canvasDOM = document.getElementById('measure-canvas')
    if (this.canvasDOM) {
      this.measureMode = true
      this.width = this.canvasDOM.width
      this.height = this.canvasDOM.height
      this.ctx = this.canvasDOM.getContext('2d')
      for (let i = 0; i < this.AreaSet.length; ++i) {
        this.AreaSet[i].updatePointSet()
      }

      if (!this.currArea) {
        this.currArea = new Area(this.canvasDOM, this.camera)
        this.currArea.addMPoint(this.cursorX, this.cursorY)
      }

      this.inCanvas = true
      this.paint = true // 更新绘图
      if (this.ua.platform !== 'pc') {
        this.canvasDOM.addEventListener('touchstart', this.bindTouch, { passive: false })
      } else {
        this.canvasDOM.addEventListener('mousemove', this.bindMouseMove, { passive: false })
        this.canvasDOM.addEventListener('mousedown', this.bindMouseDown, { passive: false })
      }
    } else {
      console.error('没拿到canvas的dom元素')
    }
  }

  /**
   * 清屏
   */
  clean () {
    this.AreaSet.length = 0
    this.currArea.pointSet.length = 0
    this.state = this.enumState.free
    this.endPaintListener.invoke()
    this.currArea = new Area(this.canvasDOM, this.camera)
    this.currArea.addMPoint(this.cursorX, this.cursorY)
    this.inCanvas = true
    this.paint = true
  }

  /**
   * 离开测量模式
   */
  exit () {
    this.confirm()
    this.measureMode = false
    if (this.ua.platform !== 'pc') {
      this.canvasDOM.removeEventListener('touchstart', this.bindTouch)
    } else {
      this.canvasDOM.removeEventListener('mousedown', this.bindMouseDown)
      this.canvasDOM.removeEventListener('mousemove', this.bindMouseMove)
    }
  }

  /**
   * 撤销测量模式
   */
  undo () {
    if (this.currArea.pointSet.length >= 2) {
      this.currArea.popMPoint()
      this.cursorX = this.currArea.pointSet[this.currArea.pointSet.length - 1].x
      this.cursorY = this.currArea.pointSet[this.currArea.pointSet.length - 1].y
    }
    if (this.currArea.pointSet.length < 2) {
      this.state = this.enumState.free
      this.endPaintListener.invoke()
    }
    this.inCanvas = true
    this.paint = true
  }

  /**
   * 确认当前操作，绘制一个area
   */
  confirm () {
    this.state = this.enumState.free // 设置绘画状态结束
    this.endPaintListener.invoke()
    if (this.ua.platform === 'pc') this.currArea.popMPoint()
    this.AreaSet.push(this.currArea) // areaset +1
    this.currArea.square = this.currArea.getSquare()
    this.currArea.done = true // 标志已完成

    this.currArea = new Area(this.canvasDOM, this.camera)
    this.currArea.addMPoint(this.cursorX, this.cursorY)

    this.inCanvas = true
    this.paint = true
  }

  /**
   * 更改测量单位
   * @param {} unit 单位
   */
  changeUnit (unit) {
    for (const area of this.AreaSet) {
      area.unit = unit
    }
    this.currArea.unit = unit

    this.inCanvas = true
    this.paint = true
  }

  /**
   * 是否在canvas中
   * @param {Event} event 触发事件
   */
  isCanvasUsable (event) {
    if (event.target !== this.canvasDOM) {
      this.inCanvas = false
      return false
    }
    this.inCanvas = true
    return true
  }

  /**
   * 触屏按下事件
   * @param {Event} e 触屏事件
   */
  onTouchStart (e) {
    e.preventDefault()
    if (!this.isCanvasUsable(e)) return

    this.cursorX = e.touches[0].clientX
    this.cursorY = e.touches[0].clientY

    this.dragFlag = false
    this.canvasDOM.addEventListener('touchmove', this.bindDrag, { passive: false })
    this.canvasDOM.addEventListener('touchend', this.bindTouchEnd, { passive: false })
  }

  /**
   * 拖拽事件
   */
  onDrag () {
    this.dragFlag = true
  }

  /**
   * 触屏结束事件
   * @param {Event} e 触屏结束事件
   */
  onTouchEnd (e) {
    if (!this.isCanvasUsable(e)) return

    this.canvasDOM.removeEventListener('touchend', this.bindTouchEnd)
    if (this.dragFlag) return

    if (this.state === this.enumState.free) {
      // 首次点击定下点
      this.state = this.enumState.painting
      this.startPaintListener.invoke()
    } else {
      this.currArea.addMPoint(this.cursorX, this.cursorY)
    }
    this.paint = true
    this.canvasDOM.removeEventListener('touchmove', this.bindDrag)
  }

  /**
   * 光标移动事件
   * @param {Event} e 触发事件
   */
  onMouseMove (e) {
    if (!this.isCanvasUsable(e)) return

    this.cursorX = e.offsetX // 更新鼠标坐标
    this.cursorY = e.offsetY
    this.paint = true
  }

  /**
   * 光标按下事件
   * @param {Event} e 触发事件
   */
  onMouseDown (e) {
    if (!this.isCanvasUsable(e)) return

    if (e.button === 0) {
      this.canvasDOM.addEventListener('mouseup', this.bindMouseUp, { passive: false })
    } else if (e.button === 2 && this.state === this.enumState.painting) {
      // 右键 按下就结束
      this.confirm() // 更新画面
    }
  }

  /**
   * 光标弹起事件
   * @param {Event} e 触发事件
   */
  onMouseUp (e) {
    if (!this.isCanvasUsable(e)) return

    if (this.state === this.enumState.free) {
      // 首次点击定下点
      this.state = this.enumState.painting
      this.startPaintListener.invoke()
    }
    this.currArea.addMPoint(this.cursorX, this.cursorY)
    this.paint = true
    this.canvasDOM.removeEventListener('mouseup', this.bindMouseUp)
  }

  /**
   * 交互更新
   */
  interactUpdate () {
    if (this.paint) {
      this.paint = false
      this.ctx.clearRect(0, 0, this.width, this.height) // 清空

      this.changeCursor() // 画动态点

      this.drawStatic()
    }
  }

  /**
   * 绘制已完成区域
   */
  drawStatic () {
    // 静态的已经框选的区域
    for (let i = 0; i < this.AreaSet.length; ++i) {
      this.AreaSet[i].drawArea()
    }
    // 静态的正在框选但未闭合的区域
    if (this.currArea) { this.currArea.drawArea() }
  }

  /**
   * 绘制光标
   */
  changeCursor () {
    // 动态点，变更area点集里最后一个点位
    this.currArea.pointSet[this.currArea.pointSet.length - 1].x = this.cursorX
    this.currArea.pointSet[this.currArea.pointSet.length - 1].y = this.cursorY
    this.currArea.physicalPointSet[this.currArea.physicalPointSet.length - 1] = this.currArea.getPhysicalPoint(this.cursorX, this.cursorY)
  }

  /**
   * canvas更新
   */
  update () {
    if (!this.showMeasure) return
    if (this.inCanvas) {
      this.interactUpdate()
    }
  }
}
